import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import useQuizSubmissionImport from 'hooks/useQuizSubmissionImport';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import quizService from 'services/quiz';

export const PageContext = createContext();

const PageProvider = ({ children }) => {
    const { quizzes, setQuizzes } = useApp();
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const history = useHistory();
    const { id } = useParams();
    const { t } = useTranslation('quiz');
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const quiz = useMemo(() => quizzes?.find((item) => item.id === id), [id, quizzes]);
    const canManageQuiz = auth?.role === 'instructor';
    const submissionImport = useQuizSubmissionImport({ quiz, auth });

    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoadFailed(false);

        quizService
            .getQuiz(id)
            .then((result) => {
                if (!active || !result) return;
                setQuizzes((current) => {
                    const items = current || [];
                    return items.some((item) => item.id === id)
                        ? items.map((item) => (item.id === id ? result : item))
                        : [...items, result];
                });
            })
            .catch((error) => {
                if (!active) return;
                setLoadFailed(true);
                const status = error?.status ?? error?.response?.status;
                if (status === 404) {
                    setQuizzes((current) => (current || []).filter((item) => item.id !== id));
                } else {
                    message.error(t('view.load-error', 'The quiz could not be loaded. Please try again.'));
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
        };
    }, [id, message, setQuizzes, t]);

    const handleBack = () => history.push('/quizzes');

    const handleEdit = () => {
        if (!canManageQuiz || !quiz) return;
        history.push(`/quizzes/${quiz.id}/edit`);
    };

    return (
        <PageContext.Provider
            value={{
                quiz,
                loading,
                loadFailed,
                canManageQuiz,
                submissionImport,
                onBack: handleBack,
                onEdit: handleEdit,
            }}
        >
            {children}
        </PageContext.Provider>
    );
};

export default PageProvider;
